import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { requireFeature } from '@/common/utils/requireFeature';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CalendarDays, Loader2, CheckCircle2, Unlink } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { officeCalendarService } from '@/features/office-calendar/services/office-calendar.service';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_authenticated/office-calendar-config')({
  beforeLoad: () => requireFeature('VIRTUAL_OFFICE'),
  component: OfficeCalendarConfigPage,
});

const QUERY_KEY = ['office-calendar-integration-config'];

function OfficeCalendarConfigPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState<string | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => officeCalendarService.getConfig(),
  });

  const updateMutation = useMutation({
    mutationFn: (id: string | null) => officeCalendarService.updateConfig(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setCalendarId(null);
      toast({
        title: id ? 'Agenda do Office conectada' : 'Agenda do Office desligada',
      });
    },
    onError: (err: any) => {
      toast({
        title: err?.response?.data?.message ?? 'Erro ao salvar',
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const calendars = config?.calendars ?? [];
  const selected = calendarId ?? config?.calendarId ?? '';

  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Agenda do Office</h1>
          <p className="text-muted-foreground mt-2">
            Escolha a agenda Google usada por todos no Asseto Office para ver e criar reuniões.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />
              Agenda compartilhada
            </CardTitle>
            <CardDescription>
              Usa as contas Google já conectadas em{' '}
              <Link to="/calendar-config" className="underline">
                Configuração de Calendários
              </Link>
              . Para usar outra conta, conecte-a lá primeiro.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              {config?.connected ? (
                <Badge className="gap-1 bg-green-600 hover:bg-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Conectada
                  {config.connectedEmail ? ` · ${config.connectedEmail}` : ''}
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  Não conectada
                </Badge>
              )}
            </div>
            {calendars.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhuma conta Google conectada no sistema.
              </p>
            ) : (
              <div className="space-y-2 max-w-xl">
                <Label htmlFor="office-calendar-select">Calendário</Label>
                <Select value={selected} onValueChange={setCalendarId}>
                  <SelectTrigger id="office-calendar-select">
                    <SelectValue placeholder="Escolha um calendário" />
                  </SelectTrigger>
                  <SelectContent>
                    {calendars.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name === c.email ? c.name : `${c.name} · ${c.email}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={() => updateMutation.mutate(selected)}
                disabled={updateMutation.isPending || !selected || selected === config?.calendarId}
              >
                {updateMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar
              </Button>
              {config?.connected && (
                <Button
                  variant="outline"
                  onClick={() => updateMutation.mutate(null)}
                  disabled={updateMutation.isPending}
                >
                  <Unlink className="h-4 w-4 mr-2" />
                  Desligar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  );
}
